/**
 * 体验期学习报告 —— 试用到期召回消息「查看详情」的落地内容。
 *
 * ## 它为什么在付费页上
 *
 * 微信新版模板消息只渲染三行短关键词（每行约 26 字就截断），卡片上塞不下任何说服力。
 * 于是分工变成：**卡片负责让家长愿意点，这里负责说服。**
 * 家长点的是「学习报告提醒」，点进来就得真的先看到一份学习报告 ——
 * 直接甩付费墙是标题党，会把这次点击和下一次的信任一起花掉。
 *
 * ## 两条克制的规矩
 *
 * 1. **拿不到数据就什么都不渲染**，页面退化成原来的付费页。
 *    报告是加分项，绝不能因为它挂了而让家长连买都买不了。
 * 2. **只陈述观察值，不下"进步了"的结论**。跨技能、跨难度的正确率变化不构成因果，
 *    包装成进步是在透支信任；样本太小时（<5 题）连观察值都不显示。
 */

import React, { useState, useEffect } from 'react';
import { service } from 'service';
import styles from './TrialReport.module.scss';

interface SkillStat {
  app: string;
  name: string;
  answered: number;
  correct: number;
}

export interface TrialReportData {
  available: boolean;
  days: number;
  startDate: string;
  endDate: string;
  expired: boolean;
  answered: number;
  correct: number;
  accuracy: number;
  activeDays: number;
  durationText: string;
  early: { answered: number; rate: number };
  late: { answered: number; rate: number };
  skills: SkillStat[];
}

// 前后半程的最小样本。和服务端 buildExpireNotifyText 的 MIN_SAMPLE 保持一致：
// 三道题算出来的"正确率提升"经不起看，说了反而减分。
const MIN_SAMPLE = 5;

export default function TrialReport({ mid }: { mid?: string }) {
  const [data, setData] = useState<TrialReportData | null>(null);

  // 刻意不用 hook/index 的 useRequest：它在 catch 里会 alert(err.message)。
  // 报告只是加分项，接口挂了绝不能给家长弹一个报错框、更不能挡住付费按钮。
  // 这里所有异常都咽掉，页面安静地退化成原来的付费页。
  useEffect(() => {
    let alive = true;
    service
      .get(`/wx/trial/expireReport${mid ? `?mid=${encodeURIComponent(mid)}` : ''}`)
      .then(res => {
        if (!alive) return;
        if (res && res.data && res.data.status === 0) {
          setData(res.data.data as TrialReportData);
        }
      })
      .catch(() => { /* 静默：报告拿不到就不显示 */ });
    return () => { alive = false; };
  }, [mid]);

  if (!data || !data.available) {
    return null;
  }
  // 一道题都没做的不该被当成"用得很好"来展示：那种恭维家长一眼看得穿，
  // 而且会让紧接着的价格显得更不值。这种情况直接不出报告。
  if (!data.answered) {
    return null;
  }

  const showTrend = data.early.answered >= MIN_SAMPLE && data.late.answered >= MIN_SAMPLE;

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div className={styles.title}>体验期学习报告</div>
        <div className={styles.range}>
          {data.startDate} - {data.endDate}
          <span className={styles.tag}>{data.expired ? `${data.days}天体验已结束` : `${data.days}天体验中`}</span>
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <div className={styles.num}>{data.answered}</div>
          <div className={styles.label}>做题（道）</div>
        </div>
        <div className={styles.metric}>
          <div className={styles.num}>{data.accuracy}<span className={styles.unit}>%</span></div>
          <div className={styles.label}>正确率</div>
        </div>
        <div className={styles.metric}>
          <div className={styles.num}>{data.activeDays}</div>
          <div className={styles.label}>天有练习</div>
        </div>
      </div>

      {
        (data.durationText || showTrend) && (
          <div className={styles.notes}>
            {data.durationText && <div className={styles.note}>累计练习 {data.durationText}</div>}
            {
              showTrend && (
                <div className={styles.note}>
                  前半程答对率 {data.early.rate}%，后半程 {data.late.rate}%
                </div>
              )
            }
          </div>
        )
      }

      {
        data.skills && data.skills.length > 0 && (
          <div className={styles.skills}>
            <div className={styles.skillsTitle}>练了这些</div>
            {
              data.skills.map(s => (
                <div className={styles.skillRow} key={s.app || s.name}>
                  <span className={styles.skillName}>{s.name}</span>
                  <span className={styles.skillNum}>{s.answered} 道 · 对 {s.correct} 道</span>
                </div>
              ))
            }
          </div>
        )
      }

      {
        data.expired && (
          <div className={styles.footer}>
            体验期已结束，开通会员可以继续不限量练习
          </div>
        )
      }
    </div>
  );
}
